import { useEffect, useState, useRef } from 'react';
import { APIProvider, Map, AdvancedMarker, InfoWindow, Polygon, useMap, useMapsLibrary, useApiLoadingStatus, APILoadingStatus } from '@vis.gl/react-google-maps';
import { useVisibleStore } from '../hooks/useVisibleStore';
import { PointInfoBubble } from './PointInfoBubble';
import { ChevronLeft, ChevronRight, X, Info as InfoIcon, Search, Music, MapPin } from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { PointIcon } from './icons/PointIcon';
import { buildHighlight, boundsOf, type Bbox, type HighlightGeometry } from '../lib/placeHighlight';

/** Lieu actuellement recherché, avec son contour s'il en a un. */
type SearchFocus = { lat: number; lng: number; zoomLevel: number; placeName?: string; bbox?: Bbox };

const getApiKey = () => {
  // Vite environment variables
  if (typeof import.meta !== 'undefined' && import.meta.env && (import.meta as any).env.VITE_GOOGLE_MAPS_PLATFORM_KEY) {
    return (import.meta as any).env.VITE_GOOGLE_MAPS_PLATFORM_KEY;
  }

  // Fallback for bundlers that replace process.env.GOOGLE_MAPS_PLATFORM_KEY directly
  try {
    const key = process.env.GOOGLE_MAPS_PLATFORM_KEY;
    if (key) return key;
  } catch (e) {
    // Ignore ReferenceError if process is not defined
  }

  return '';
};

const API_KEY = getApiKey();
const hasValidKey = Boolean(API_KEY) && API_KEY !== 'YOUR_API_KEY';
const MAPBOX_TOKEN = (import.meta as any).env.VITE_MAPBOX_ACCESS_TOKEN || '';

interface MapViewProps {
  isTeamView?: boolean;
}

export function MapView({ isTeamView = false }: MapViewProps) {
  if (!hasValidKey) {
    return (
      <div className="flex items-center justify-center h-full font-sans p-6">
        <div className="text-center max-w-lg bg-[#14161B] p-8 border border-[#2D3139] rounded-xl shadow-2xl">
          <h2 className="text-xl font-bold text-white mb-4">Google Maps API Key Required</h2>
          <div className="text-slate-300 text-sm space-y-4 text-left">
            <p><strong>Step 1:</strong> <a className="text-emerald-400 hover:underline" href="https://console.cloud.google.com/google/maps-apis/start" target="_blank" rel="noopener noreferrer">Get an API Key</a></p>
            <p><strong>Step 2:</strong> Add your key as a secret in AI Studio:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Open <strong>Settings</strong> (⚙️ gear icon, <strong>top-right corner</strong>)</li>
              <li>Select <strong>Secrets</strong></li>
              <li>Type <code className="bg-[#0A0B0E] p-1 rounded">GOOGLE_MAPS_PLATFORM_KEY</code> as the secret name, press <strong>Enter</strong></li>
              <li>Paste your API key as the value, press <strong>Enter</strong></li>
            </ul>
            <div className="mt-4 p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
              <h3 className="font-semibold text-indigo-400 mb-2">Note pour le déploiement en production (Ex: Firebase Hosting) :</h3>
              <p className="text-xs text-slate-300 mb-2">Si vous avez déployé sur Firebase Hosting ou Vercel, vous devez ajouter la variable d'environnement avec le préfixe <strong>VITE_</strong> :</p>
              <ul className="text-xs list-disc pl-5 text-slate-300">
                <li>Allez dans les réglages de votre projet (ex: Environment Variables)</li>
                <li>Ajoutez une variable nommée : <code className="bg-[#0A0B0E] px-1 rounded text-emerald-400 font-mono">VITE_GOOGLE_MAPS_PLATFORM_KEY</code></li>
                <li>Collez votre clé API dedans</li>
                <li><strong>Redéployez</strong> votre application pour que Vite puisse l'intégrer au build.</li>
              </ul>
              <p className="text-xs text-slate-400 mt-3 border-t border-indigo-500/20 pt-2">Concernant les restrictions Google Cloud : assurez-vous d'avoir autorisé votre domaine (par ex: <code>*.bipteams.space/*</code> ou <code>https://www.bipteams.space/*</code>).</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <APIProvider apiKey={API_KEY} version="weekly" libraries={['places', 'geocoding']}>
      <MapViewInner isTeamView={isTeamView} />
    </APIProvider>
  );
}

function MapViewInner({ isTeamView = false }: MapViewProps) {
  const { points, allPoints, updatePoint, mapFocus, setMapFocus, currentUser, shifts: _shifts, issues, team, impersonatedTeamId } = useVisibleStore();
  // Une recherche de lieu porte un nom (et souvent un contour) ; un clic sur un point non.
  const isSearchFocus = Boolean(mapFocus && (mapFocus.placeName || mapFocus.bbox));
  const effectiveTeamId = impersonatedTeamId || currentUser.teamId;
  const isTeam = isTeamView || currentUser.role === 'staff';
  
  // Restricted access visibility filter: restricted points are only visible to team who have a shift assigned to them there.
  const visiblePoints = points.filter(p => {
    if ((isTeamView || currentUser.role === 'staff') && p.isRestricted) {
      return _shifts.some(s => s.teamId === effectiveTeamId && s.pointId === p.id);
    }
    return true;
  });

  const [selectedPointId, setSelectedPointId] = useState<string | null>(null);
  const [activeBubblePointId, setActiveBubblePointId] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isFocusDrawerOpen, setIsFocusDrawerOpen] = useState(false);
  const [descriptionModal, setDescriptionModal] = useState<{ name: string; markdown: string } | null>(null);
  const [pointSearchQuery, setPointSearchQuery] = useState('');

  const myShiftPointIds = new Set(_shifts.filter(s => s.teamId === effectiveTeamId).map(s => s.pointId));
  const myNextShifts = _shifts
    .filter(s => s.teamId === effectiveTeamId && new Date(s.end) > new Date())
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  // For each focus point, find the earliest team shift → sort focus buttons chronologically
  const earliestShiftAtPoint = (pointId: string): number => {
    const shift = myNextShifts.find(s => s.pointId === pointId);
    return shift ? new Date(shift.start).getTime() : Infinity;
  };

  const teamFocusPoints = visiblePoints
    .filter(p => p.isMapFocus && p.location && myShiftPointIds.has(p.id))
    .sort((a, b) => earliestShiftAtPoint(a.id) - earliestShiftAtPoint(b.id));

  // Focus map on the first upcoming shift point for team/artists
  useEffect(() => {
    if (isTeam && effectiveTeamId) {
      const targetTeam = team.find(st => st.id === effectiveTeamId);
      // Include parent artist shifts for sub-members
      const myShifts = _shifts.filter(s => s.teamId === effectiveTeamId || (targetTeam?.parentArtistId && s.teamId === targetTeam.parentArtistId));
      const isArtist = targetTeam?.isArtist === true;
      const visiblePointIds = new Set(visiblePoints.map(p => p.id));

      const filteredMyShifts = myShifts.filter(s => {
        const pt = (allPoints || points).find(p => p.id === s.pointId);
        if (!pt || !pt.location) return false;
        if (!visiblePointIds.has(pt.id)) return false;
        if (isArtist && !pt.isStage) return false;
        return true;
      });

      // Find earliest upcoming (or most recent past) shift
      const upcoming = filteredMyShifts
        .filter(s => new Date(s.end) > new Date())
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
      const past = filteredMyShifts
        .filter(s => new Date(s.end) <= new Date())
        .sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime());
      const firstShift = upcoming[0] || past[0];

      if (firstShift) {
        const pt = (allPoints || points).find(p => p.id === firstShift.pointId && p.location);
        if (pt && pt.location) {
          // Only refocus if not already focused on the FIRST upcoming shift point
          const isAlreadyOnFirst = mapFocus
            && Math.abs(pt.location.lat - mapFocus.lat) < 0.0001
            && Math.abs(pt.location.lng - mapFocus.lng) < 0.0001;
          if (!isAlreadyOnFirst) {
            setMapFocus({ lat: pt.location!.lat, lng: pt.location!.lng, zoomLevel: 18 });
          }
        }
      }
    }
  }, [effectiveTeamId, isTeamView, currentUser.role, _shifts, points, allPoints, team]);

  // Handle map click to place/move the selected operational point
  const handleMapClick = (e: any) => {
    setActiveBubblePointId(null);
    if (!isSidebarOpen || !selectedPointId || !e.detail.latLng) return;
    updatePoint(selectedPointId, {
      location: { lat: e.detail.latLng.lat, lng: e.detail.latLng.lng }
    });
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(v => {
      const newValue = !v;
      if (!newValue) setSelectedPointId(null);
      return newValue;
    });
  };

  return (
    <div className="flex h-full overflow-hidden relative font-sans">
      <style>{`
        @keyframes slow-hop {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }
        .animate-slow-hop {
          animation: slow-hop 4s ease-in-out infinite;
        }
      `}</style>
      
      {/* Sidebar for operational points */}
      {currentUser.role !== 'staff' && (
        <div className={`flex-none bg-[#14161B]/95 backdrop-blur-xl border-r border-[#2D3139] flex flex-col transition-all duration-300 ease-in-out z-30 shadow-2xl overflow-hidden
          ${isSidebarOpen ? 'w-80 translate-x-0' : 'w-0 -translate-x-full'}`}>
          <div className="p-5 border-b border-[#2D3139]/50 bg-gradient-to-b from-[#1A1D23] to-transparent flex flex-col gap-3 whitespace-nowrap">
            <div>
              <h3 className="font-bold text-white text-lg tracking-wide">Placer un Point</h3>
              <p className="text-xs text-slate-400 mt-1 whitespace-normal leading-relaxed">Sélectionnez un point ci-dessous puis cliquez sur la carte.</p>
            </div>
            
            {/* Search Bar for OP Points */}
            <div className="relative w-full">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="w-4 h-4 text-slate-400" />
              </span>
              <input
                type="text"
                value={pointSearchQuery}
                onChange={e => setPointSearchQuery(e.target.value)}
                placeholder="Rechercher un point..."
                className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl pl-9 pr-8 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all font-medium"
              />
              {pointSearchQuery && (
                <button
                  onClick={() => setPointSearchQuery('')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-white"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar">
            {(() => {
              const sidebarFilteredPoints = visiblePoints.filter(p =>
                p.name.toLowerCase().includes(pointSearchQuery.toLowerCase())
              );
              return (
                <>
                  {sidebarFilteredPoints.map(p => (
                    <button
                      key={p.id}
                      onClick={() => setSelectedPointId(p.id)}
                      className={`w-full text-left p-4 rounded-xl text-sm transition-all duration-300 whitespace-nowrap overflow-hidden text-ellipsis shadow-sm hover:-translate-y-0.5 ${
                        selectedPointId === p.id 
                          ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/40 shadow-indigo-500/10' 
                          : p.isStage
                            ? 'bg-amber-500/5 text-slate-300 hover:bg-amber-500/10 border border-amber-500/30 hover:border-amber-500/50'
                            : 'text-slate-300 hover:bg-[#1A1D23] border border-[#2D3139]/50 hover:border-[#3D414D]'
                      }`}
                    >
                      <div className="font-bold truncate text-[13px] flex items-center justify-between">
                        <span>{p.name}</span>
                        {p.isStage && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 uppercase tracking-widest font-black flex items-center gap-0.5">
                            <Music className="w-2.5 h-2.5" /> Scène
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] mt-1.5 uppercase tracking-wider font-semibold flex items-center gap-1.5">
                        {p.location ? (
                          <><span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> <span className="text-emerald-400">Placé</span></>
                        ) : (
                          <><span className="w-1.5 h-1.5 rounded-full bg-slate-600"></span> <span className="text-slate-500">Non Placé</span></>
                        )}
                      </div>
                    </button>
                  ))}
                  {sidebarFilteredPoints.length === 0 && (
                    <div className="text-center py-8 text-xs text-slate-500 italic">
                      Aucun point trouvé
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Button to toggle sidebar */}
      {currentUser.role !== 'staff' && (
        <button 
          onClick={toggleSidebar}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-40 bg-[#1A1D23]/90 backdrop-blur border border-l-0 border-[#2D3139] text-slate-300 rounded-r-xl p-2.5 shadow-xl hover:text-white hover:bg-indigo-600/20 hover:border-indigo-500/50 transition-all duration-300"
          style={{ transform: `translateY(-50%) translateX(${isSidebarOpen ? '20rem' : '0'})` }}
          title={isSidebarOpen ? "Fermer le menu" : "Ouvrir le menu pour placer des points"}
        >
          {isSidebarOpen ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
      )}

      {/* Right-side Focus Drawer Button */}
      {currentUser.role !== 'staff' && (
        <button 
          onClick={() => setIsFocusDrawerOpen(!isFocusDrawerOpen)}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-40 bg-[#1A1D23]/90 backdrop-blur border border-r-0 border-[#2D3139] text-slate-300 rounded-l-xl p-2.5 shadow-xl hover:text-white hover:bg-indigo-600/20 hover:border-indigo-500/50 transition-all duration-300"
          style={{ transform: `translateY(-50%) translateX(${isFocusDrawerOpen ? '-18rem' : '0'})` }}
        >
          {isFocusDrawerOpen ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
        </button>
      )}

      {/* Right-side Focus Drawer */}
      {currentUser.role !== 'staff' && (
        <div className={`fixed inset-y-0 right-0 z-30 w-72 bg-[#14161B]/95 backdrop-blur-xl border-l border-[#2D3139] flex flex-col transition-all duration-300 ease-in-out shadow-2xl overflow-hidden
          ${isFocusDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="p-5 border-b border-[#2D3139]/50 bg-gradient-to-b from-[#1A1D23] to-transparent whitespace-nowrap">
            <h3 className="font-bold text-white text-lg tracking-wide">Points Focus</h3>
            <p className="text-xs text-slate-400 mt-1">Accès rapide aux points stratégiques.</p>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
            {visiblePoints.filter(p => p.isMapFocus && p.location).map(p => (
              <button
                key={p.id}
                onClick={() => {
                  setMapFocus({ lat: p.location!.lat, lng: p.location!.lng, zoomLevel: 18 });
                  setActiveBubblePointId(p.id);
                }}
                className="w-full bg-[#1A1D23] hover:bg-indigo-500/20 text-slate-300 hover:text-indigo-300 border border-[#2D3139] hover:border-indigo-500/50 p-4 rounded-xl text-sm font-bold transition-all shadow-sm text-left"
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 relative w-full h-full overflow-hidden">
        <Map
          defaultCenter={mapFocus ? { lat: mapFocus.lat, lng: mapFocus.lng } : 
            ((isTeamView || currentUser.role === 'staff') && teamFocusPoints.length > 0
              ? teamFocusPoints[0].location 
              : (visiblePoints.find(p => p.location)?.location || { lat: 48.8566, lng: 2.3522 }))}
          defaultZoom={mapFocus ? mapFocus.zoomLevel : 
            ((isTeamView || currentUser.role === 'staff') && teamFocusPoints.length > 0 ? 17 : 
              (visiblePoints.some(p => p.location) ? 15 : 13))}
          mapId="DEMO_MAP_ID"
          internalUsageAttributionIds={['gmp_mcp_codeassist_v1_aistudio']}
          style={{width: '100%', height: '100%'}}
          onClick={handleMapClick}
          gestureHandling="greedy"
          mapTypeControl={true}
          mapTypeControlOptions={{ position: 3 }} // TOP_RIGHT
          // Un lieu recherché est cadré sur son contour par `SearchHighlight` :
          // on ne force alors ni le centre ni le zoom, sinon le recadrage serait écrasé.
          center={mapFocus && !isSearchFocus ? { lat: mapFocus.lat, lng: mapFocus.lng } : undefined}
          zoom={mapFocus && !isSearchFocus ? mapFocus.zoomLevel : undefined}
          onCameraChanged={(ev) => {
            setMapFocus({
              lat: ev.detail.center.lat,
              lng: ev.detail.center.lng,
              zoomLevel: ev.detail.zoom,
              // Conserver le repère de recherche : un déplacement de carte ne doit pas l'effacer
              placeName: mapFocus?.placeName,
              bbox: mapFocus?.bbox,
            });
          }}
          disableDefaultUI={true}
        >
          <SearchHighlight focus={mapFocus} />
          {visiblePoints.filter(p => p.location).map(p => {
            const hasPublicIncident = issues.some(i => i.pointId === p.id && !i.resolved && (i.type === 'incident_public' || i.type === 'alert'));
            
            // Check if there are active artist performances
            const pointShifts = _shifts.filter(s => s.pointId === p.id);
            const hasPerformance = pointShifts.some(s => {
              const assignedTeam = team.find(st => st.id === s.teamId);
              return s.shiftType === 'performance' || assignedTeam?.isArtist === true;
            });

            // Status calculation
            const now = new Date();
            const teamShifts = _shifts.filter(s => s.teamId === effectiveTeamId && s.pointId === p.id);
            const activeShift = teamShifts.find(s => now >= new Date(s.start) && now <= new Date(s.end));
            
            const allTeamShifts = _shifts
              .filter(s => s.teamId === effectiveTeamId && new Date(s.end) > now)
              .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
            
            const nextShift = allTeamShifts.find(s => new Date(s.start) > now);
            const isNextPoint = nextShift?.pointId === p.id;
            const isPastPoint = teamShifts.length > 0 && teamShifts.every(s => now > new Date(s.end));

            let markerClass = "";
            let iconColor = undefined;

            if (p.isStage) {
              markerClass = "flex items-center justify-center transition-all cursor-pointer animate-slow-hop";
              if (selectedPointId === p.id) {
                markerClass += " text-white z-10 scale-125";
                iconColor = "#ffffff";
              } else if (hasPublicIncident) {
                markerClass += " text-red-500 z-25 scale-125";
                iconColor = "#ef4444";
              } else if (hasPerformance) {
                markerClass += " text-amber-400 scale-135 z-20";
                iconColor = "#f59e0b";
              } else if (activeShift) {
                markerClass += " text-orange-500 scale-135 z-20";
                iconColor = "#f97316";
              } else if (isNextPoint) {
                markerClass += " text-emerald-400 scale-125 z-10";
                iconColor = "#10b981";
              } else if (isPastPoint) {
                markerClass += " text-slate-500 opacity-40 grayscale";
                iconColor = "#64748b";
              } else {
                markerClass += " text-amber-500 hover:text-amber-400 hover:scale-135";
                iconColor = "#f59e0b";
              }
            } else {
              markerClass = "p-1.5 rounded-full shadow-md border flex items-center justify-center transition-all hover:scale-110";
              if (selectedPointId === p.id) {
                markerClass += " bg-indigo-500 border-white z-10";
                iconColor = "#ffffff";
              } else if (hasPublicIncident) {
                markerClass += " bg-red-500/20 border-red-500 text-red-500 shadow-red-500/20 z-20 scale-110";
                iconColor = "#ef4444";
              } else if (hasPerformance) {
                markerClass += " bg-amber-500/20 border-amber-500 text-amber-400 shadow-amber-500/40 scale-125 z-20 animate-[pulse_1.5s_infinite]";
                iconColor = "#f59e0b";
              } else if (activeShift) {
                markerClass += " bg-orange-500/20 border-orange-500 shadow-orange-500/40 scale-125 z-20";
                iconColor = "#f97316";
              } else if (isNextPoint) {
                markerClass += " bg-emerald-500/20 border-emerald-500 shadow-emerald-500/40 scale-110 z-10 animate-[pulse_3s_infinite]";
                iconColor = "#10b981";
              } else if (isPastPoint) {
                markerClass += " bg-slate-800/50 border-slate-700 opacity-40 grayscale";
                iconColor = "#64748b";
              } else {
                markerClass += " bg-[#14161B] border-[#2D3139] text-indigo-400";
              }
            }

            return (
              <AdvancedMarker 
                key={p.id} 
                position={p.location!} 
                title={p.name}
                onClick={() => {
                  if (activeBubblePointId === p.id) {
                    setActiveBubblePointId(null);
                  } else {
                    setActiveBubblePointId(p.id);
                  }
                }}
              >
                <div className="relative">
                  {hasPublicIncident && (
                    <div className="absolute -inset-2 bg-red-500/40 rounded-full radar-pulsate pointer-events-none z-0" />
                  )}
                  {hasPerformance && !hasPublicIncident && (
                    <div className="absolute -inset-2 bg-amber-500/40 rounded-full radar-pulsate-gold pointer-events-none z-0" />
                  )}
                  <div className={`relative z-10 ${markerClass}`}>
                    <PointIcon name={p.iconName || 'MapPin'} size={activeShift || hasPerformance ? 22 : 18} color={iconColor} />
                  </div>
                </div>
              </AdvancedMarker>
            );
          })}

          {activeBubblePointId && visiblePoints.find(p => p.id === activeBubblePointId)?.location && (
            <InfoWindow
              position={visiblePoints.find(p => p.id === activeBubblePointId)!.location}
              onCloseClick={() => setActiveBubblePointId(null)}
              headerDisabled={true}
            >
              <PointInfoBubble
                point={visiblePoints.find(p => p.id === activeBubblePointId)!}
                isTeamView={isTeamView}
                onShowDescription={(p) => setDescriptionModal({ name: p.name, markdown: p.descriptionMarkdown! })}
              />
            </InfoWindow>
          )}
        </Map>

        {/* Retractable Map Search Bar */}
        {!isTeam && (
          <div className="absolute top-0 left-1/2 -translate-x-1/2 z-[40] w-80 max-w-[90vw] flex flex-col items-center">
            <div className={`w-full bg-[#14161B]/95 backdrop-blur-xl border-x border-b border-[#2D3139] rounded-b-2xl px-4 shadow-2xl transition-all duration-500 ease-in-out ${
              isSearchOpen ? 'max-h-[400px] opacity-100 translate-y-0 overflow-visible pt-2 pb-4' : 'max-h-0 pt-0 pb-0 opacity-0 -translate-y-4 border-none pointer-events-none overflow-hidden'
            }`}>
              <div className="text-left mb-2 flex items-center justify-between">
                <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Rechercher un lieu</span>
                <button 
                  onClick={() => setIsSearchOpen(false)} 
                  className="text-slate-500 hover:text-white p-1"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              <GoogleAutocompleteSearch onLocationSelected={(lat, lng, details) => {
                setMapFocus({ lat, lng, zoomLevel: 18, placeName: details?.name, bbox: details?.bbox });
                setIsSearchOpen(false);
              }} />
            </div>
            {/* Loop / Toggle Handle Button */}
            <div className="flex justify-center -mt-px">
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className={`bg-[#14161B]/95 backdrop-blur-xl border-x border-b border-[#2D3139] px-5 py-2 rounded-b-2xl text-slate-400 hover:text-white transition-all flex items-center gap-2 shadow-2xl active:pt-2.5 hover:border-indigo-500/50 ${
                  isSearchOpen ? 'border-t border-[#2D3139]/40' : ''
                }`}
                title="Rechercher un lieu"
              >
                <Search className={`w-3.5 h-3.5 transition-transform duration-500 ${isSearchOpen ? 'rotate-90 text-indigo-400 font-bold' : 'text-slate-400'}`} />
                <span className="text-[9px] font-extrabold tracking-widest uppercase">
                  {isSearchOpen ? "Fermer" : "Rechercher"}
                </span>
              </button>
            </div>
          </div>
        )}
        
        {selectedPointId && (
           <div className="absolute top-20 left-4 bg-[#14161B] border border-indigo-500 text-indigo-300 px-4 py-2 rounded-lg shadow-lg text-sm font-medium z-10 pointer-events-none">
             Cliquez sur la carte pour placer "{visiblePoints.find(p => p.id === selectedPointId)?.name}"
           </div>
        )}

        {/* Description Modal */}
        {descriptionModal && (
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm z-[500] flex items-center justify-center p-4" onClick={() => setDescriptionModal(null)}>
            <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
              <div className="p-5 border-b border-[#2D3139] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <InfoIcon className="w-4 h-4 text-emerald-400" />
                  <h3 className="font-bold text-white">{descriptionModal.name}</h3>
                </div>
                <button onClick={() => setDescriptionModal(null)} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-[#2D3139]"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-6 custom-scrollbar prose prose-invert prose-sm max-w-none">
                <Markdown remarkPlugins={[remarkGfm, remarkBreaks]}>{descriptionModal.markdown}</Markdown>
              </div>
              <div className="p-4 border-t border-[#2D3139]">
                <button onClick={() => setDescriptionModal(null)} className="w-full py-2.5 bg-[#1A1D23] hover:bg-[#2D3139] text-white font-medium rounded-xl text-sm transition-colors">Fermer</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Contexte renvoyé avec la position : nom du lieu et contour, s'il en a un. */
type SearchDetails = { name?: string; bbox?: Bbox };

interface SearchProps {
  onLocationSelected: (lat: number, lng: number, details?: SearchDetails) => void;
  placeholder?: string;
  googleError?: string;
}

function GoogleAutocompleteSearch({ onLocationSelected }: SearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [useFallback, setUseFallback] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);
  
  const apiStatus = useApiLoadingStatus();
  const placesLib = useMapsLibrary('places');
  const geocodingLib = useMapsLibrary('geocoding');
  
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);

  useEffect(() => {
    if (apiStatus === APILoadingStatus.FAILED) {
      console.warn("Google Maps API failed to load. Falling back to Mapbox Geocoding search.");
      setGoogleError("Le script Google Maps n'a pas pu être chargé (vérifiez la clé ou les restrictions).");
      setUseFallback(true);
    }
  }, [apiStatus]);

  // Loading timeout (2.5s) to guarantee Mapbox fallback if Google Places gets stuck/blocked
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!placesLib || !geocodingLib) {
        console.warn("Google Places/Geocoding library loading timed out. Falling back to Mapbox.");
        setGoogleError("Délai d'attente dépassé pour les librairies Google Places/Geocoding.");
        setUseFallback(true);
      }
    }, 2500);
    return () => clearTimeout(timer);
  }, [placesLib, geocodingLib]);

  useEffect(() => {
    if (placesLib) {
      autocompleteServiceRef.current = new placesLib.AutocompleteService();
    }
  }, [placesLib]);

  useEffect(() => {
    if (geocodingLib) {
      geocoderRef.current = new geocodingLib.Geocoder();
    }
  }, [geocodingLib]);

  const fallbackToMapboxGeocoding = async (query: string) => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&limit=1`
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} (${response.statusText || 'Forbidden'})`);
      }
      const data = await response.json();
      if (data.features && data.features[0]) {
        const feature = data.features[0];
        const [lng, lat] = feature.center;
        const bbox: Bbox | undefined =
          Array.isArray(feature.bbox) && feature.bbox.length === 4 ? feature.bbox as Bbox : undefined;
        onLocationSelected(lat, lng, { name: feature.place_name, bbox });
        setSearchQuery(feature.place_name);
      }
    } catch (e: any) {
      console.error('Fallback geocoding error:', e);
      setGoogleError(prev => `${prev ? prev + ' + ' : ''}Géocodage Mapbox échoué: ${e.message || 'Erreur'}`);
    }
  };

  if (useFallback) {
    return (
      <MapboxGeocodingSearch 
        onLocationSelected={onLocationSelected} 
        placeholder="Rechercher un lieu (Mapbox - Google non disponible)..." 
        googleError={googleError || "Service Google indisponible"}
      />
    );
  }

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }

    if (autocompleteServiceRef.current) {
      try {
        autocompleteServiceRef.current.getPlacePredictions(
          { input: query },
          (predictions, status) => {
            if (status === 'OK' && predictions) {
              setSearchResults(predictions);
              setGoogleError(null);
            } else {
              setSearchResults([]);
              // Fallback to Mapbox search if Google Places API returns an error status (like REQUEST_DENIED)
              if (status !== 'ZERO_RESULTS') {
                console.warn(`Google Places API returned status: ${status}. Falling back to Mapbox Geocoding search.`);
                setGoogleError(`Erreur API Google Places: ${status}`);
                setUseFallback(true);
              }
            }
          }
        );
      } catch (err: any) {
        console.error("Error in getPlacePredictions, falling back to Mapbox:", err);
        setGoogleError(`Exception Google: ${err.message || err}`);
        setUseFallback(true);
      }
    } else {
      if (apiStatus === APILoadingStatus.LOADED && !placesLib) {
        console.warn("Google Maps loaded but Places library is missing. Falling back to Mapbox.");
        setGoogleError("Librairie Places manquante après chargement.");
        setUseFallback(true);
      }
    }
  };

  const selectSearchResult = (result: google.maps.places.AutocompletePrediction) => {
    if (geocoderRef.current) {
      geocoderRef.current.geocode({ placeId: result.place_id }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const lat = results[0].geometry.location.lat();
          const lng = results[0].geometry.location.lng();
          // Le viewport du résultat est son contour : c'est lui qui permet de le situer sur la carte
          const sw = results[0].geometry.viewport?.getSouthWest();
          const ne = results[0].geometry.viewport?.getNorthEast();
          const bbox: Bbox | undefined = sw && ne
            ? [sw.lng(), sw.lat(), ne.lng(), ne.lat()]
            : undefined;
          onLocationSelected(lat, lng, { name: result.description, bbox });
          setSearchResults([]);
          setSearchQuery(result.description);
        } else {
          console.warn(`Google Geocoding failed with status: ${status}. Trying Mapbox Geocoding fallback.`);
          setGoogleError(`Erreur géocodage Google: ${status}`);
          fallbackToMapboxGeocoding(result.description);
        }
      });
    } else {
      setGoogleError("Géocodeur Google non disponible pour la sélection.");
      fallbackToMapboxGeocoding(result.description);
    }
  };

  const isLoading = apiStatus === APILoadingStatus.LOADING || !placesLib || !geocodingLib;

  return (
    <div className="mt-1.5 relative">
      <input
        type="text"
        disabled={isLoading}
        value={isLoading ? '' : searchQuery}
        onChange={(e) => handleSearch(e.target.value)}
        placeholder={isLoading ? "Chargement de la recherche Google..." : "Rechercher un lieu (Google)..."}
        className={`w-full bg-[#0A0B0E] border text-white text-sm px-4 py-2.5 rounded-xl focus:outline-none transition-all ${
          isLoading ? 'border-[#2D3139]/40 opacity-60 cursor-not-allowed' : 'border-[#2D3139] focus:border-indigo-500/50'
        }`}
      />
      {searchResults.length > 0 && (
        <div className="absolute left-0 right-0 mt-2 bg-[#1A1D23] border border-[#2D3139] rounded-xl shadow-2xl z-50 max-h-60 overflow-y-auto flex flex-col whitespace-normal">
          {searchResults.map((result, i) => (
            <button
              key={i}
              onClick={() => selectSearchResult(result)}
              className="w-full text-left px-4 py-3 text-xs text-slate-300 hover:bg-indigo-500/20 hover:text-white border-b border-[#2D3139]/50 last:border-0 transition-colors"
            >
              {result.description}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MapboxGeocodingSearch({ onLocationSelected, placeholder, googleError }: SearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [mapboxError, setMapboxError] = useState<string | null>(null);

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&limit=5`
      );
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} (${response.statusText || 'Forbidden'})`);
      }
      const data = await response.json();
      setSearchResults(data.features || []);
      setMapboxError(null);
    } catch (e: any) {
      console.error('Geocoding error:', e);
      setMapboxError(e.message || 'Erreur de recherche');
    }
  };

  const selectSearchResult = (result: any) => {
    const [lng, lat] = result.center;
    const bbox: Bbox | undefined =
      Array.isArray(result.bbox) && result.bbox.length === 4 ? result.bbox as Bbox : undefined;
    onLocationSelected(lat, lng, { name: result.place_name, bbox });
    setSearchResults([]);
    setSearchQuery(result.place_name);
  };

  return (
    <div className="mt-1.5 relative">
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => handleSearch(e.target.value)}
        placeholder={placeholder || "Rechercher un lieu (Mapbox)..."}
        className="w-full bg-[#0A0B0E] border border-[#2D3139] text-white text-sm px-4 py-2.5 rounded-xl focus:outline-none focus:border-indigo-500/50 transition-all"
      />
      {googleError && (
        <div className="mt-1.5 text-[9px] text-amber-400 font-semibold px-2.5 py-1 bg-amber-500/5 border border-amber-500/10 rounded-lg flex items-center gap-1.5 leading-tight">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></span>
          <span>Google non dispo : {googleError}</span>
        </div>
      )}
      {mapboxError && (
        <div className="mt-1 text-[9px] text-rose-400 font-semibold px-2.5 py-1 bg-rose-500/5 border border-rose-500/10 rounded-lg flex items-center gap-1.5 leading-tight">
          <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0"></span>
          <span>Mapbox non dispo : {mapboxError}</span>
        </div>
      )}
      {searchResults.length > 0 && (
        <div className="absolute left-0 right-0 mt-2 bg-[#1A1D23] border border-[#2D3139] rounded-xl shadow-2xl z-50 max-h-60 overflow-y-auto flex flex-col whitespace-normal">
          {searchResults.map((result, i) => (
            <button
              key={i}
              onClick={() => selectSearchResult(result)}
              className="w-full text-left px-4 py-3 text-xs text-slate-300 hover:bg-indigo-500/20 hover:text-white border-b border-[#2D3139]/50 last:border-0 transition-colors"
            >
              {result.place_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Surlignage du lieu recherché sur la carte Google Maps.
 * Rendu à l'intérieur du `<Map>` : `useMap()` n'a de sens que dans ce contexte.
 *
 * Le contour est figé au moment de la recherche. La carte réécrit `mapFocus`
 * à chaque déplacement (`onCameraChanged`) : recalculer le contour depuis
 * `mapFocus` le ferait dériver et relancerait un recadrage en boucle.
 */
function SearchHighlight({ focus }: { focus: SearchFocus | null }) {
  const map = useMap();
  const [highlight, setHighlight] = useState<{
    signature: string;
    placeName?: string;
    center: { lat: number; lng: number };
    geometry: HighlightGeometry;
  } | null>(null);

  useEffect(() => {
    const isSearch = Boolean(focus && (focus.placeName || focus.bbox));
    if (!isSearch) {
      setHighlight(null);
      return;
    }

    const signature = `${focus!.placeName ?? ''}|${(focus!.bbox ?? []).join(',')}`;
    setHighlight(prev => {
      if (prev?.signature === signature) return prev; // même recherche : contour inchangé
      return {
        signature,
        placeName: focus!.placeName,
        center: { lat: focus!.lat, lng: focus!.lng },
        geometry: buildHighlight([focus!.lng, focus!.lat], focus!.bbox),
      };
    });
  }, [focus]);

  const signature = highlight?.signature;

  useEffect(() => {
    if (!map || !highlight) return;
    const [[minLng, minLat], [maxLng, maxLat]] = boundsOf(highlight.geometry);
    // Cadre la carte sur le contour plutôt que sur le seul point
    map.fitBounds({ south: minLat, west: minLng, north: maxLat, east: maxLng }, 70);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, signature]);

  if (!highlight) return null;

  return (
    <>
      <Polygon
        paths={highlight.geometry.coordinates[0].map(([lng, lat]) => ({ lat, lng }))}
        fillColor="#6366f1"
        fillOpacity={0.25}
        strokeColor="#818cf8"
        strokeWeight={2.5}
        clickable={false}
      />
      {highlight.placeName && (
        <AdvancedMarker position={highlight.center}>
          <div className="relative pointer-events-none">
            <div className="absolute -inset-2 bg-indigo-500/40 rounded-full radar-pulsate" />
            <div className="relative z-10 w-6 h-6 rounded-full bg-indigo-500 border-2 border-white shadow-lg flex items-center justify-center">
              <MapPin className="w-3.5 h-3.5 text-white" />
            </div>
          </div>
        </AdvancedMarker>
      )}
    </>
  );
}
